import moment from 'moment';

import { CampaignType } from 'containers/Rule/form-v2/CampaignRuleFormModal/CampaignRuleFormModal';

import { TOrbit } from '../../../i18n/TModules';
import { WizardCampaignType, WizardTrigger } from '../wizardModel';

const TW = `${TOrbit}.wizard`;

export type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface CampaignRuleCondition {
  attribute: string;
  operator: string;
  selected_values: unknown;
  attribute_key?: string;
  attribute_type?: string;
}

/**
 * A rule as the module's rule form holds it: `temp` drives its schema, and the module drops it on
 * save (`CampaignRuleFormModal` `normalizeValues`).
 */
export interface CampaignRuleValues {
  name: string;
  temp: { trigger_type: WizardTrigger['kind']; event_name: string };
  event_name: string;
  event_params: Record<string, unknown>;
  conditions: CampaignRuleCondition[];
  [key: string]: unknown;
}

const AMOUNT = /^-?\d+(\.\d{1,2})?$/;
const WHOLE = /^\d+$/;

/** The rule schema's own name for each wizard type. */
export const ruleSchemaType = (type: WizardCampaignType): CampaignType =>
  type === 'instant_outcome' ? 'instant_reward' : type === 'game' ? 'stamp' : type;

const label = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

const validProperties = (trigger: WizardTrigger) =>
  trigger.properties.filter(row => row.key.trim() !== '' && row.value.trim() !== '');

const conditionsOf = (trigger: WizardTrigger): CampaignRuleCondition[] => {
  if (trigger.kind !== 'transaction') {
    return [];
  }
  const rows: CampaignRuleCondition[] = [];
  if (trigger.amountOn && AMOUNT.test(trigger.amount)) {
    rows.push({
      attribute: 'amount',
      operator: 'greater_than_or_equal_to',
      selected_values: Number(trigger.amount)
    });
  }
  if (trigger.typeOn && trigger.transactionType !== null) {
    rows.push({
      attribute: 'transaction_type',
      operator: 'equals',
      selected_values: trigger.transactionType
    });
  }
  if (trigger.scheduleOn) {
    if (trigger.days.length > 0) {
      rows.push({
        attribute: 'transaction_date',
        operator: 'day_of_week',
        selected_values: trigger.days
      });
    }
    if (trigger.months.length > 0) {
      rows.push({
        attribute: 'transaction_date',
        operator: 'in_months',
        selected_values: trigger.months
      });
    }
    if (trigger.deadline !== null) {
      rows.push({
        attribute: 'transaction_date',
        operator: 'less_than_or_equal_to',
        selected_values: trigger.deadline
      });
    }
  }
  if (trigger.propertiesOn) {
    validProperties(trigger).forEach(row =>
      rows.push({
        attribute: 'transaction_properties',
        attribute_key: row.key.trim(),
        attribute_type: 'String',
        operator: row.operator,
        selected_values: row.value.trim()
      })
    );
  }
  return rows;
};

const eventOf = (
  trigger: WizardTrigger
): { event_name: string; option: string; event_params: Record<string, unknown> } => {
  switch (trigger.kind) {
    case 'transaction':
      return {
        event_name: 'transactions_created',
        option: 'transactions_created',
        event_params: {}
      };
    case 'loyalty':
      return {
        event_name: 'points_issued',
        option: 'points_issued',
        event_params: trigger.loyalty === null ? {} : { loyalty_id: trigger.loyalty.id }
      };
    case 'milestone':
      return {
        event_name: trigger.activity,
        option: trigger.activity,
        event_params: trigger.campaign === null ? {} : { campaign_id: trigger.campaign.id }
      };
    case 'reward':
      return {
        event_name: 'voucher_issued',
        option: 'voucher_issued_reward',
        event_params:
          trigger.rewardActivity === 'specific' && trigger.reward !== null
            ? { reward_id: trigger.reward.id }
            : {}
      };
    default:
      return {
        event_name: 'app_trigger',
        option: 'app_trigger',
        event_params:
          trigger.appTrigger === null ? {} : { automation_trigger_id: trigger.appTrigger.id }
      };
  }
};

const formatDay = (value: string) => moment.parseZone(value).format('D MMM YYYY');

/** A transaction trigger's checked conditions, and its property rows apart from them. */
export const transactionParts = (
  trigger: WizardTrigger,
  t: Translate
): { conditions: string[]; properties: { key: string; operator: string; value: string }[] } => {
  const S = `${TW}.summary`;
  const conditions: string[] = [];
  if (trigger.amountOn && trigger.amount !== '') {
    conditions.push(t(`${S}.amount`, { value: trigger.amount }));
  }
  if (trigger.typeOn && trigger.transactionType !== null) {
    conditions.push(t(`${S}.type`, { value: trigger.transactionType }));
  }
  if (trigger.scheduleOn) {
    if (trigger.days.length > 0) {
      conditions.push(t(`${S}.days`, { value: trigger.days.map(label).join(', ') }));
    }
    if (trigger.months.length > 0) {
      conditions.push(t(`${S}.months`, { value: trigger.months.map(label).join(', ') }));
    }
    if (trigger.deadline !== null) {
      conditions.push(t(`${S}.deadline`, { value: formatDay(trigger.deadline) }));
    }
  }
  const properties = trigger.propertiesOn
    ? validProperties(trigger).map(row => ({
        key: row.key.trim(),
        operator: t(`${S}.${row.operator === 'equals' ? 'equals' : 'notEquals'}`),
        value: row.value.trim()
      }))
    : [];
  return { conditions, properties };
};

/** The card title and one-line summary the task / rule lists, the review and the rule name use. */
export const describeTrigger = (
  trigger: WizardTrigger,
  t: Translate
): { title: string; detail: string } => {
  const S = `${TW}.summary`;
  switch (trigger.kind) {
    case 'transaction': {
      const { conditions, properties } = transactionParts(trigger, t);
      const parts = [...conditions, ...properties.map(row => t(`${S}.property`, row))];
      return { title: t(`${S}.transaction`), detail: parts.join(` ${t(`${S}.and`)} `) };
    }
    case 'loyalty':
      return { title: t(`${TW}.action.loyalty`), detail: t(`${S}.loyalty`) };
    case 'milestone':
      return {
        title: t(`${TW}.action.milestone`),
        detail:
          trigger.campaign === null
            ? t(`${TW}.trigger.milestone.${trigger.activity}`)
            : t(`${S}.campaign`, {
                activity: t(`${TW}.trigger.milestone.${trigger.activity}`),
                name: trigger.campaign.name,
                id: trigger.campaign.id
              })
      };
    case 'reward':
      return {
        title: t(`${TW}.action.reward`),
        detail:
          trigger.rewardActivity === 'specific' && trigger.reward !== null
            ? t(`${S}.rewardSpecific`, { name: trigger.reward.name, id: trigger.reward.id })
            : t(`${TW}.trigger.reward.any`)
      };
    default:
      return {
        title: t(`${TW}.action.others`),
        detail: t(`${S}.others`, { name: trigger.appTrigger?.name ?? '' })
      };
  }
};

/** What the trigger form still needs before Next. */
export const triggerErrors = (
  trigger: WizardTrigger,
  type: WizardCampaignType,
  t: Translate
): string[] => {
  const errors: string[] = [];
  const TT = `${TW}.trigger`;
  switch (trigger.kind) {
    case 'transaction': {
      if (trigger.amountOn && trigger.amount !== '' && !AMOUNT.test(trigger.amount)) {
        errors.push(t(`${TT}.transaction.amountInvalid`));
      } else if (conditionsOf(trigger).length === 0) {
        errors.push(t(`${TT}.transaction.empty`));
      }
      break;
    }
    case 'milestone':
      if (trigger.campaign === null) errors.push(t(`${TT}.milestone.selectCampaign`));
      break;
    case 'reward':
      if (trigger.rewardActivity === 'specific' && trigger.reward === null) {
        errors.push(t(`${TT}.reward.selectReward`));
      }
      break;
    case 'others':
      if (trigger.appTrigger === null) errors.push(t(`${TT}.others.placeholder`));
      break;
    default:
      break;
  }
  if (type === 'progress') {
    if (!WHOLE.test(trigger.points)) {
      errors.push(t(`${TT}.progress.pointsRequired`));
    } else if (
      trigger.earnType === 'variable' &&
      (!WHOLE.test(trigger.base) || Number(trigger.base) < 1 || !WHOLE.test(trigger.cap))
    ) {
      errors.push(t(`${TT}.progress.variableRequired`));
    }
  }
  return errors;
};

/**
 * One wizard trigger as the module's rule form would hold it. The rule name follows the FR name
 * generators: the trigger and its conditions.
 */
export const toCampaignRule = (
  trigger: WizardTrigger,
  type: WizardCampaignType,
  t: Translate
): CampaignRuleValues => {
  const { event_name, option, event_params } = eventOf(trigger);
  const { title, detail } = describeTrigger(trigger, t);
  const rule: CampaignRuleValues = {
    name: `${title} ${detail}`.trim(),
    temp: { trigger_type: trigger.kind, event_name: option },
    event_name,
    event_params,
    conditions: conditionsOf(trigger)
  };
  if (type === 'stamp') {
    return { ...rule, outcome_value: 1, rule_trigger_days: null };
  }
  if (type === 'progress') {
    return {
      ...rule,
      action_name: 'issue_points',
      earn_type: trigger.earnType,
      outcome_value: Number(trigger.points),
      ...(trigger.earnType === 'variable'
        ? { outcome_base: Number(trigger.base), max_outcome_value: Number(trigger.cap) }
        : {})
    };
  }
  return rule;
};

/** The rule as the module saves it: no form-only `temp`. */
export const toSavedRule = ({ temp, ...rule }: CampaignRuleValues): Record<string, unknown> => rule;
